/**
 * Fills leftover collection-grid cells after optional in-grid cards
 * (Kore Bundles CTA, etc.) are injected. Extra products come from the
 * next page so a 16-product page + 1 bundle becomes a complete row.
 */
class CollectionGridFill {
  constructor() {
    this.busy = false;
    this.gridObserver = null;
    this.containerObserver = null;
    this.boundGrid = null;
    this.resizeTimer = null;

    this.bindGrid();
    this.observeContainer();
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimer);
      this.resizeTimer = setTimeout(() => this.fill(this.boundGrid), 150);
    });
  }

  observeContainer() {
    const container = document.getElementById('ProductGridContainer');
    if (!container || this.containerObserver) return;

    this.containerObserver = new MutationObserver(() => {
      const grid = document.getElementById('product-grid');
      if (grid && grid !== this.boundGrid) this.bindGrid();
    });
    this.containerObserver.observe(container, { childList: true, subtree: true });
  }

  bindGrid() {
    const grid = document.getElementById('product-grid');
    if (!grid) return;

    this.boundGrid = grid;
    if (this.gridObserver) this.gridObserver.disconnect();

    this.gridObserver = new MutationObserver(() => this.fill(grid));
    this.gridObserver.observe(grid, { childList: true });
    this.fill(grid);
  }

  storageKey() {
    const params = new URLSearchParams(window.location.search);
    params.delete('page');
    return `collection-grid-fill:${window.location.pathname}?${params}`;
  }

  currentPage() {
    return Number(new URLSearchParams(window.location.search).get('page') || 1);
  }

  fillerMap() {
    try {
      return JSON.parse(sessionStorage.getItem(this.storageKey()) || '{}');
    } catch (error) {
      return {};
    }
  }

  saveFillers(page, ids) {
    const map = this.fillerMap();
    map[page] = ids;
    sessionStorage.setItem(this.storageKey(), JSON.stringify(map));
  }

  stolenIds() {
    const page = this.currentPage();
    const ids = new Set();
    Object.entries(this.fillerMap()).forEach(([fromPage, pageIds]) => {
      if (Number(fromPage) < page) {
        (pageIds || []).forEach((id) => ids.add(String(id)));
      }
    });
    return ids;
  }

  productId(item) {
    if (item.dataset.productId) return String(item.dataset.productId);
    const link = item.querySelector('[id^="CardLink-"]');
    if (!link) return '';
    const parts = link.id.split('-');
    return parts[parts.length - 1] || '';
  }

  isBundle(item) {
    return /kore-bundle/.test(item.className) || Boolean(item.querySelector('[class*="kore-bundle"]'));
  }

  isVisible(item) {
    return item.dataset.gridStolen !== 'true' && item.style.display !== 'none';
  }

  columnCount(grid) {
    const item = [...grid.children].find((child) => this.isVisible(child) && child.getBoundingClientRect().width > 0);
    if (!item) return 1;

    const styles = window.getComputedStyle(grid);
    const gap = parseFloat(styles.columnGap) || 0;
    const itemWidth = item.getBoundingClientRect().width;
    if (itemWidth <= 0) return 1;
    return Math.max(1, Math.round((grid.clientWidth + gap) / (itemWidth + gap)));
  }

  hideStolenProducts(grid) {
    const stolen = this.stolenIds();
    [...grid.children].forEach((item) => {
      if (this.isBundle(item)) return;
      const id = this.productId(item);
      if (id && stolen.has(id)) {
        item.style.display = 'none';
        item.dataset.gridStolen = 'true';
      }
    });
  }

  // Items that span several grid columns or rows (hub bundle tiles) fill one cell per column and row.
  cellWeight(item) {
    const styles = window.getComputedStyle(item);
    const span = (value) => {
      const match = /span\s+(\d+)/.exec(value || '');
      return match ? Number(match[1]) : 1;
    };
    const columns = Math.max(span(styles.gridColumnStart), span(styles.gridColumnEnd));
    const rows = Math.max(span(styles.gridRowStart), span(styles.gridRowEnd));
    return columns * rows;
  }

  slotsNeeded(grid) {
    const count = [...grid.children]
      .filter((item) => this.isVisible(item))
      .reduce((total, item) => total + this.cellWeight(item), 0);
    const columns = this.columnCount(grid);
    if (count === 0 || columns < 2) return 0;
    const remainder = count % columns;
    return remainder === 0 ? 0 : columns - remainder;
  }

  async fill(grid) {
    if (this.busy || !grid || grid.dataset.gridFillExhausted === 'true') return;

    this.hideStolenProducts(grid);
    const needed = this.slotsNeeded(grid);
    if (needed === 0) return;

    const nextUrl = grid.dataset.nextUrl;
    if (!nextUrl) return;

    this.busy = true;
    let addedIds = [];

    try {
      const url = new URL(nextUrl, window.location.origin);
      url.searchParams.set('section_id', grid.dataset.id);

      const response = await fetch(url.toString());
      if (!response.ok) return;

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const nextGrid = doc.getElementById('product-grid');
      if (!nextGrid) return;

      const existingIds = new Set(
        [...grid.children].map((item) => this.productId(item)).filter(Boolean)
      );

      for (const item of nextGrid.children) {
        if (addedIds.length >= needed) break;
        if (this.isBundle(item)) continue;

        const id = this.productId(item);
        if (!id || existingIds.has(id)) continue;

        const clone = document.importNode(item, true);
        clone.dataset.gridFiller = 'true';
        grid.appendChild(clone);
        existingIds.add(id);
        addedIds.push(id);
      }

      if (addedIds.length) this.saveFillers(this.currentPage(), addedIds);

      if (typeof initializeScrollAnimationTrigger === 'function') {
        initializeScrollAnimationTrigger(grid);
      }
    } catch (error) {
      console.warn('[collection-grid-fill] could not fill leftover slots', error);
    } finally {
      this.busy = false;
      if (addedIds.length === 0) {
        grid.dataset.gridFillExhausted = 'true';
        return;
      }
      this.fill(grid);
    }
  }
}

function initCollectionGridFill() {
  if (document.getElementById('product-grid')) {
    new CollectionGridFill();
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initCollectionGridFill);
} else {
  initCollectionGridFill();
}
